// Haptics (§8.2 rumble, §8.3 touch vibration, §26): subscribes to the sim events and plays the
// feedback for moments that involve the local bean. Rumble goes to the active gamepad when the
// pad is the current device, `navigator.vibrate` when touch is. Replays never rumble.
//
// | Moment                   | Pad (weak, strong, ms) | Touch vibrate |
// |--------------------------|------------------------|---------------|
// | Your shove/hit lands     | 0.3, 0, 60             | 15 ms         |
// | You are hit              | 0, 0.6, 120            | 40 ms         |
// | Bomb near you / any KO   | 1.0, 1.0, 250          | 80 ms         |
//   you score or suffer      |                        |               |
// | Teetering                | 0.2 pulse every 150 ms | —             |
import { NO_SLOT, TUNE } from '@shared/constants';
import { EV, type SimEvent } from '@shared/events';
import { BS } from '@shared/ids';
import { onSim, type Bus } from '../core/bus';
import type { Settings } from '../profile/settings';
import type { InputDevice } from './glyphs';

export type RumbleKind = 'light' | 'hit' | 'heavy' | 'teeter';

export const RUMBLE: Readonly<Record<RumbleKind, { weak: number; strong: number; ms: number; vibrateMs: number }>> = {
  light: { weak: 0.3, strong: 0, ms: 60, vibrateMs: 15 },
  hit: { weak: 0, strong: 0.6, ms: 120, vibrateMs: 40 },
  heavy: { weak: 1, strong: 1, ms: 250, vibrateMs: 80 },
  teeter: { weak: 0.2, strong: 0, ms: 50, vibrateMs: 0 },
};
export const TEETER_PULSE_MS = 150;
/** A bomb or potato blast this close to the local bean counts as "near you" (blast radius + 1.5 m). */
export const BLAST_NEAR_M = TUNE.bomb.radius + 1.5;
/** Safety stop for teeter pulses if no state update arrives (the sim's teeter lasts 27 ticks). */
const TEETER_MAX_MS = (TUNE.edge.teeterTicks / 60) * 1000 + 300;

export interface HapticsDeps {
  bus: Bus;
  settings(): Settings;
  device(): InputDevice;
  localSlot(): number;
  /** The local bean's rendered position and state, or null when it has none. */
  local(): { x: number; y: number; state: number } | null;
  rumble(weak: number, strong: number, ms: number): void;
  vibrate(ms: number): void;
  now(): number;
}

export class Haptics {
  private teeterUntil = -1;
  private nextPulse = 0;
  private readonly off: () => void;
  /** Kinds played, newest last (tests and the debug page read it). */
  readonly log: RumbleKind[] = [];

  constructor(private readonly deps: HapticsDeps) {
    this.off = onSim(deps.bus, (ev, _local, replay) => {
      if (!replay) this.onEvent(ev);
    });
  }

  dispose(): void {
    this.off();
  }

  /** Plays one feedback moment on the current device (scaled by the settings). */
  play(kind: RumbleKind): void {
    const d = this.deps;
    const spec = RUMBLE[kind];
    const device = d.device();
    this.log.push(kind);
    if (this.log.length > 32) this.log.shift();
    if (device === 'pad') {
      const scale = d.settings().controls.pad.rumble / 100;
      if (scale > 0) d.rumble(spec.weak * scale, spec.strong * scale, spec.ms);
    } else if (device === 'touch' && spec.vibrateMs > 0 && d.settings().controls.touch.haptics) {
      d.vibrate(spec.vibrateMs);
    }
  }

  /** Teeter pulses; call once per frame. */
  update(): void {
    if (this.teeterUntil < 0) return;
    const now = this.deps.now();
    const me = this.deps.local();
    if (now > this.teeterUntil || (me !== null && me.state !== BS.TEETER && now > this.teeterUntil - TEETER_MAX_MS + 120)) {
      this.teeterUntil = -1;
      return;
    }
    if (now >= this.nextPulse) {
      this.nextPulse = now + TEETER_PULSE_MS;
      this.play('teeter');
    }
  }

  private onEvent(ev: SimEvent): void {
    const me = this.deps.localSlot();
    if (me === NO_SLOT) return;
    switch (ev.type) {
      case EV.HIT:
      case EV.PLUNGER_HIT:
      case EV.PEBBLE_HIT:
        if (ev.b === me) this.play('hit');
        else if (ev.a === me && ev.b !== NO_SLOT) this.play('light');
        break;
      case EV.KO:
        if (ev.a === me || ev.b === me) this.play('heavy');
        break;
      case EV.BOMB_BOOM:
      case EV.POTATO_BOOM: {
        const pos = this.deps.local();
        if ((ev.type === EV.POTATO_BOOM && ev.a === me) || (pos && Math.hypot(ev.x - pos.x, ev.y - pos.y) <= BLAST_NEAR_M)) this.play('heavy');
        break;
      }
      case EV.TEETER:
        if (ev.a === me) {
          const now = this.deps.now();
          this.teeterUntil = now + TEETER_MAX_MS;
          this.nextPulse = now;
        }
        break;
      case EV.TEETER_SAVE:
      case EV.FALL:
      case EV.LEDGE:
        if (ev.a === me) this.teeterUntil = -1;
        break;
      default:
        break;
    }
  }
}
